import { memoize, once } from 'lodash'
import MarkdownIt, { type Options } from 'markdown-it'

const getRenderer = memoize(MarkdownIt)

export function markdownToHtml(text: string, options: Options = { breaks: true }): string {
  return getRenderer(options).render(text)
}

/**
 * Get a MarkdownIt instance configured for restricted inline rendering.
 *
 * Calling `.render()` on this instance can still generate paragraphs. To skip them completely, use `.renderInline()`.
 */
// Uses Lodash's `once` so that this function is executed once then cached
const getInlineRender = once(() => {
  return MarkdownIt(
    // The `zero` rules preset gives us complete control over what formatting to enable
    'zero',
    // Required for the `newline` rule
    { breaks: true },
  ).enable([
    // Replace \n with <br>
    'newline',
    // Convert anything that looks like a URL to a link
    'autolink',
    // Convert `code` to <code>code</code>
    'backticks',
    // Enable **bold** __bold__ *italic* _italic_
    'emphasis',
    // Enable escaping characters with a backslash
    'escape',
    // Enable links with [text](url)
    'link',
  ])
})

/**
 * Renders a limited subset of inline Markdown formatting to HTML,
 * as defined in the worker configuration specification for `WorkerConfigurationField.help_text`.
 *
 * @param text Markdown text to render.
 * @returns Rendered HTML.
 */
export function inlineMarkdownToHtml(text: string): string {
  return getInlineRender().renderInline(text)
}
